'use client';

import { useState, useEffect } from 'react';

interface DeadlineCountdownProps {
  /** Deadline name (localized) */
  deadlineName: string;
  /** University/entity display name */
  entityName: string;
  /** ISO date string (YYYY-MM-DD) of the deadline */
  deadlineDate: string;
  /** Language code */
  lang: string;
}

function computeDaysUntil(dateStr: string): number {
  // Use WIB (UTC+7) as reference timezone for Indonesian students
  const now = new Date();
  const wibOffset = 7 * 60; // minutes
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  const wibNow = new Date(utcMs + wibOffset * 60000);
  const today = new Date(wibNow.getFullYear(), wibNow.getMonth(), wibNow.getDate());
  const target = new Date(dateStr + 'T00:00:00');
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

export function DeadlineCountdown({ deadlineName, entityName, deadlineDate, lang }: DeadlineCountdownProps) {
  const [daysLeft, setDaysLeft] = useState<number | null>(null);

  useEffect(() => {
    setDaysLeft(computeDaysUntil(deadlineDate));
    // Update at midnight WIB
    const interval = setInterval(() => {
      setDaysLeft(computeDaysUntil(deadlineDate));
    }, 60000); // check every minute
    return () => clearInterval(interval);
  }, [deadlineDate]);

  if (daysLeft === null || daysLeft < 0) return null;

  const isId = lang === 'id';
  const isUrgent = daysLeft <= 7;
  const isToday = daysLeft === 0;

  const message = isToday
    ? (isId ? `Hari ini! ${deadlineName} di ${entityName}` : `Today! ${deadlineName} at ${entityName}`)
    : isId
      ? `${daysLeft} hari lagi sampai ${deadlineName} di ${entityName}`
      : `${daysLeft} days until ${deadlineName} at ${entityName}`;

  return (
    <div
      className={`mx-auto max-w-6xl px-4 md:px-8 ${isUrgent ? '' : ''}`}
      role="status"
      aria-live="polite"
    >
      <div className={`rounded-xl border px-4 py-3 text-center text-sm font-medium ${
        isUrgent
          ? 'panel-error text-semantic-error'
          : 'panel-warning text-semantic-warning'
      }`}>
        <span className="mr-1.5">{isUrgent ? '🔴' : '⏳'}</span>
        {isToday ? (
          <strong>{message}</strong>
        ) : (
          <>
            <strong>{daysLeft}</strong> {message.slice(String(daysLeft).length)}
          </>
        )}
      </div>
    </div>
  );
}
